import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Calendar, Plus, CheckCircle, XCircle, Clock, Briefcase } from "@phosphor-icons/react";

const LEAVE_TYPES = ["Casual", "Sick", "Earned", "Maternity", "Paternity", "Comp-Off", "LOP"];
const STATUS_COLORS = {
  pending:  { bg: "bg-[var(--status-warning)]/10", text: "text-[var(--status-warning)]", lbl: "Pending" },
  approved: { bg: "bg-[var(--status-success)]/10", text: "text-[var(--status-success)]", lbl: "Approved" },
  rejected: { bg: "bg-[var(--status-danger)]/10",  text: "text-[var(--status-danger)]",  lbl: "Rejected" },
};

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—";

export default function LeavePage() {
  const [dash, setDash] = useState(null);
  const [showApply, setShowApply] = useState(false);
  const [error, setError] = useState(null);

  const load = () => api.get("/hr/me/dashboard").then(r => setDash(r.data)).catch(e => setError(e.message));
  useEffect(() => { load(); }, []);

  if (error) return <div className="p-8 text-[var(--status-danger)]">Failed to load: {error}</div>;
  if (!dash) return <div className="p-8 text-[var(--text-muted)]">Loading…</div>;

  const totalEntitled = Object.values(dash.balance).reduce((s, b) => s + b.entitled, 0);
  const totalUsed = Object.values(dash.balance).reduce((s, b) => s + b.used, 0);

  return (
    <div className="p-6 space-y-6" data-testid="leave-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow">HR · Self-service</div>
          <h1 className="font-display font-bold text-3xl mt-1 text-[var(--text-primary)]">My Leave</h1>
          <div className="text-[12px] text-[var(--text-muted)] mt-1">Apply · track · view balance. Manager auto-routed via reporting hierarchy.</div>
        </div>
        <div className="flex gap-2">
          <button data-testid="apply-leave-btn" onClick={() => setShowApply(true)} className="span-btn">
            <Plus size={14} weight="bold" /> Apply for leave
          </button>
          {(dash.is_manager || (dash.pending_decisions || []).length > 0) && (
            <Link to="/hr/leave/approvals" className="span-btn span-btn-ghost" data-testid="approvals-link">
              <Briefcase size={14} /> Approvals queue
              {dash.pending_decisions.length > 0 && (
                <span className="ml-1 bg-[var(--brand-accent)] text-white text-[10px] font-bold px-1.5 rounded-full">{dash.pending_decisions.length}</span>
              )}
            </Link>
          )}
        </div>
      </div>

      {/* Balance summary */}
      <div className="bg-[var(--surface,#fff)] border border-[var(--border)] rounded p-5" data-testid="balance-panel">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-bold text-[var(--text-primary)]">YTD balance</h2>
          <div className="text-[11px] text-[var(--text-muted)]">{totalUsed} used · {totalEntitled - totalUsed} remaining of {totalEntitled}</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
          {LEAVE_TYPES.map(t => {
            const b = dash.balance[t];
            const pct = b.entitled > 0 ? (b.used / b.entitled) * 100 : 0;
            return (
              <div key={t} className="border border-[var(--border)] rounded p-2.5" data-testid={`balance-${t}`}>
                <div className="text-[10px] uppercase font-semibold text-[var(--text-muted)] tracking-wider">{t}</div>
                <div className="text-xl font-bold mt-1">{b.remaining}<span className="text-[11px] text-[var(--text-muted)]"> / {b.entitled}</span></div>
                {b.entitled > 0 && (
                  <div className="h-1 bg-[var(--surface-2)] rounded mt-1.5 overflow-hidden">
                    <div className="h-full bg-[var(--brand-primary)]" style={{ width: `${Math.min(100, pct)}%` }} />
                  </div>
                )}
                <div className="text-[10px] text-[var(--text-muted)] mt-1">{b.used} used</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* My requests */}
      <div className="bg-[var(--surface,#fff)] border border-[var(--border)] rounded p-5" data-testid="my-requests">
        <h2 className="font-bold text-[var(--text-primary)] mb-3">My recent requests</h2>
        {(dash.my_recent || []).length === 0 ? (
          <div className="text-[12px] text-[var(--text-muted)] text-center py-6">No leave requests yet. Click &quot;Apply for leave&quot; to submit one.</div>
        ) : (
          <table className="w-full text-[12px]">
            <thead>
              <tr className="border-b border-[var(--border)] text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
                <th className="text-left py-2">Type</th>
                <th className="text-left py-2">Dates</th>
                <th className="text-right py-2">Days</th>
                <th className="text-left py-2">Reason</th>
                <th className="text-left py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {dash.my_recent.map(lr => {
                const sc = STATUS_COLORS[lr.status] || STATUS_COLORS.pending;
                return (
                  <tr key={lr.leave_id} className="border-b border-[var(--border)] hover:bg-[var(--surface-2)]" data-testid={`request-${lr.leave_id}`}>
                    <td className="py-2 font-semibold">{lr.leave_type}</td>
                    <td className="py-2 font-mono text-[11px]">{fmtDate(lr.start_date)} → {fmtDate(lr.end_date)}</td>
                    <td className="py-2 text-right font-mono">{lr.days}</td>
                    <td className="py-2 text-[var(--text-secondary)] max-w-[300px] truncate">{lr.reason || "—"}</td>
                    <td className="py-2"><span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${sc.bg} ${sc.text}`}>{sc.lbl}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {showApply && <ApplyLeaveModal onClose={() => setShowApply(false)} onSaved={() => { setShowApply(false); load(); }} balance={dash.balance} />}
    </div>
  );
}

function ApplyLeaveModal({ onClose, onSaved, balance }) {
  const [form, setForm] = useState({
    leave_type: "Casual",
    start_date: new Date().toISOString().slice(0, 10),
    end_date: new Date().toISOString().slice(0, 10),
    reason: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const days = (() => {
    const s = new Date(form.start_date);
    const e = new Date(form.end_date);
    if (isNaN(s) || isNaN(e) || e < s) return 0;
    return Math.floor((e - s) / 86400000) + 1;
  })();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await api.post("/hr/leave-requests", form);
      onSaved();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || e2.message || "Submit failed");
    } finally { setBusy(false); }
  };

  const remaining = balance[form.leave_type]?.remaining || 0;
  const willExceed = days > remaining && form.leave_type !== "LOP" && form.leave_type !== "Comp-Off";

  return (
    <div className="fixed inset-0 z-[200] bg-black/50 flex items-center justify-center p-4" data-testid="apply-leave-modal">
      <form onSubmit={submit} className="bg-[var(--surface,#fff)] rounded-lg shadow-xl w-full max-w-md p-5">
        <h2 className="font-bold text-[var(--text-primary)] mb-3 flex items-center gap-2">
          <Calendar size={18} weight="duotone" className="text-[var(--brand-primary)]" /> Apply for leave
        </h2>
        <div className="space-y-3">
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1">Leave type</label>
            <select data-testid="leave-type-select" value={form.leave_type} onChange={e => setForm({ ...form, leave_type: e.target.value })} className="span-input w-full">
              {LEAVE_TYPES.map(t => (
                <option key={t} value={t}>{t} ({balance[t]?.remaining || 0} remaining)</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1">Start date</label>
              <input data-testid="leave-start" type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} className="span-input w-full" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1">End date</label>
              <input data-testid="leave-end" type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} className="span-input w-full" min={form.start_date} />
            </div>
          </div>
          <div className="text-[11px] text-[var(--text-muted)]" data-testid="days-count">
            Duration: <span className="font-bold text-[var(--text-primary)]">{days} day(s)</span>
            {willExceed && <span className="text-[var(--status-warning)] ml-2">⚠ Exceeds remaining balance ({remaining})</span>}
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1">Reason</label>
            <textarea data-testid="leave-reason" rows={3} value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} placeholder="Personal work, medical, etc." className="span-input w-full" />
          </div>
          {err && <div className="text-[12px] text-[var(--status-danger)] bg-[var(--status-danger)]/10 px-3 py-2 rounded" data-testid="leave-error">{err}</div>}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button type="button" onClick={onClose} className="span-btn span-btn-ghost">Cancel</button>
          <button data-testid="submit-leave" disabled={busy || days < 1} className="span-btn disabled:opacity-50">{busy ? "Submitting…" : "Submit request"}</button>
        </div>
      </form>
    </div>
  );
}
